import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, DollarSign, Receipt } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const DealExpenseLedger = ({ clientId, dealAmount }) => {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ total_expenses: 0, total_payments: 0, net: 0 });
  const [showAdd, setShowAdd] = useState(false);
  const [newDesc, setNewDesc] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newCategory, setNewCategory] = useState('expense');
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/ptc-finance/deal/${clientId}/ledger`);
      setEntries(res.data?.entries || []);
      setTotals({
        total_expenses: res.data?.total_expenses || 0,
        total_payments: res.data?.total_payments || 0,
        net: res.data?.net || 0,
      });
    } catch { /* ledger may not exist yet */ }
  }, [clientId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const addExpense = async () => {
    if (!newDesc.trim() || !newAmount) return;
    setSaving(true);
    try {
      await api.post(`/ptc-finance/deal/${clientId}/add-expense`, {
        description: newDesc.trim(),
        amount: parseFloat(newAmount),
        category: newCategory,
      });
      toast.success('Expense added');
      setNewDesc('');
      setNewAmount('');
      setShowAdd(false);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
    setSaving(false);
  };

  const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="text-xs" data-testid="deal-expense-ledger">
      {/* Summary row */}
      <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-slate-200">
        <div className="flex gap-3">
          <span className="text-slate-500">Expenses: <span className="font-semibold text-red-600">{fmt(totals.total_expenses)}</span></span>
          <span className="text-slate-500">Payments: <span className="font-semibold text-emerald-600">{fmt(totals.total_payments)}</span></span>
        </div>
        <span className={`font-bold ${totals.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
          Net: {fmt(totals.net)}
        </span>
      </div>

      {/* Entries list */}
      <div className="max-h-40 overflow-y-auto space-y-0.5 mb-2">
        {entries.length === 0 && (
          <p className="text-slate-400 text-center py-3">No entries yet</p>
        )}
        {entries.map((e) => (
          <div key={e.ledger_id} className="flex items-center justify-between py-1 px-1.5 hover:bg-slate-50 rounded group" data-testid={`ledger-entry-${e.ledger_id}`}>
            <div className="flex items-center gap-1.5 min-w-0">
              {e.entry_type === 'payment' ? (
                <DollarSign className="w-3 h-3 text-emerald-500 flex-shrink-0" />
              ) : (
                <Receipt className="w-3 h-3 text-red-400 flex-shrink-0" />
              )}
              <span className="text-slate-700 truncate">{e.description}</span>
              <span className="text-[10px] text-slate-400">{e.entry_type}</span>
            </div>
            <span className={`font-mono font-medium flex-shrink-0 ml-2 ${e.entry_type === 'payment' ? 'text-emerald-600' : 'text-red-600'}`}>
              {e.entry_type === 'payment' ? '+' : '-'}{fmt(e.amount)}
            </span>
          </div>
        ))}
      </div>

      {/* Add expense form */}
      {showAdd ? (
        <div className="border border-slate-200 rounded-lg p-2 space-y-1.5 bg-slate-50">
          <input
            type="text"
            placeholder="Description (e.g. Attorney fee)"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            className="w-full border rounded px-2 py-1 text-xs"
            data-testid="expense-desc-input"
            autoFocus
          />
          <div className="flex gap-1.5">
            <input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              className="flex-1 border rounded px-2 py-1 text-xs"
              data-testid="expense-amount-input"
            />
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="border rounded px-2 py-1 text-xs bg-white"
              data-testid="expense-category-select"
            >
              <option value="expense">Expense</option>
              <option value="partner_cost">Partner Cost</option>
              <option value="refund">Refund</option>
              <option value="payment">Payment In</option>
            </select>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" onClick={addExpense} disabled={saving || !newDesc.trim() || !newAmount} className="h-6 text-[11px]" data-testid="expense-save-btn">
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="h-6 text-[11px]">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} className="w-full h-6 text-[11px]" data-testid="add-expense-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Entry
        </Button>
      )}
    </div>
  );
};

export default DealExpenseLedger;
